/**
 * Format a date to a relative time string (e.g., "2 hours ago", "3 days ago")
 * @param date The date to format
 * @returns A formatted string representing the time elapsed
 */
export function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) {
    return "Just now";
  } else if (diffMin < 60) {
    return `${diffMin}${diffMin === 1 ? " minute" : " minutes"} ago`;
  } else if (diffHour < 24) {
    return `${diffHour}${diffHour === 1 ? " hour" : " hours"} ago`;
  } else if (diffDay < 7) {
    return `${diffDay}${diffDay === 1 ? " day" : " days"} ago`;
  } else if (diffWeek < 4) {
    return `${diffWeek}${diffWeek === 1 ? " week" : " weeks"} ago`;
  } else if (diffMonth < 12) {
    return `${diffMonth}${diffMonth === 1 ? " month" : " months"} ago`;
  } else {
    return `${diffYear}${diffYear === 1 ? " year" : " years"} ago`;
  }
}

/**
 * Format a date to a short relative time string (e.g., "2h", "3d")
 * @param date The date to format
 * @returns A formatted string representing the time elapsed
 */
export function formatTimeAgoShort(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) {
    return "now";
  } else if (diffMin < 60) {
    return `${diffMin}m`;
  } else if (diffHour < 24) {
    return `${diffHour}h`;
  } else if (diffDay < 7) {
    return `${diffDay}d`;
  } else if (diffWeek < 52) {
    return `${diffWeek}w`;
  } else {
    return `${diffYear}y`;
  }
}

/**
 * Format a date for display (e.g., "Jan 15, 2024")
 * @param date The date to format
 * @returns A formatted date string
 */
export function formatDate(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
}

/**
 * Format a date and time for display (e.g., "Jan 15, 2024 at 3:30 PM")
 * @param date The date to format
 * @returns A formatted date and time string
 */
export function formatDateTime(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return date.toLocaleDateString("en-US", options);
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export type TimePeriod = "Day" | "Week" | "Month" | "Year";

export function generateDateRange(period: TimePeriod, offset = 0): DateRange {
  const today = toDateOnly(new Date());
  let start: Date;
  let end: Date;

  const dayOfWeek = today.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  switch (period) {
    case "Week":
      start = toDateOnly(new Date(today));
      start.setDate(start.getDate() - diff);
      // Apply offset (negative = past weeks)
      start.setDate(start.getDate() + offset * 7);

      end = toDateOnly(new Date(start));
      end.setDate(start.getDate() + 6);
      break;

    case "Month":
      start = new Date(today.getFullYear(), today.getMonth() + offset, 1);
      end = new Date(today.getFullYear(), today.getMonth() + offset + 1, 0);
      break;

    case "Year":
      start = new Date(today.getFullYear() + offset, 0, 1);
      end = new Date(today.getFullYear() + offset, 11, 31);
      break;

    default: {
      // Day view with offset
      const dayDate = toDateOnly(new Date(today));
      dayDate.setDate(dayDate.getDate() + offset);
      return {
        startDate: formatYYYYMMDD(dayDate),
        endDate: formatYYYYMMDD(dayDate),
      };
    }
  }

  return {
    startDate: formatYYYYMMDD(start),
    endDate: formatYYYYMMDD(end),
  };
}

/**
 * Get a human-readable label for the current time period with offset
 * @param period The time period type
 * @param offset The offset from current period (negative = past)
 * @returns A formatted string label
 */
export function getPeriodLabel(period: TimePeriod, offset = 0): string {
  const today = new Date();

  switch (period) {
    case "Year": {
      const year = today.getFullYear() + offset;
      return `${year}`;
    }

    case "Month": {
      const monthDate = new Date(today.getFullYear(), today.getMonth() + offset, 1);
      return monthDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    }

    case "Week": {
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - diff + offset * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const startMonth = weekStart.toLocaleDateString("en-US", { month: "short" });
      const endMonth = weekEnd.toLocaleDateString("en-US", { month: "short" });
      const startDay = weekStart.getDate();
      const endDay = weekEnd.getDate();
      const year = weekEnd.getFullYear();

      if (startMonth === endMonth) {
        return `${startMonth} ${startDay} - ${endDay}, ${year}`;
      }
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
    }

    case "Day": {
      const dayDate = new Date(today);
      dayDate.setDate(dayDate.getDate() + offset);
      if (offset === 0) return "Today";
      if (offset === -1) return "Yesterday";
      return dayDate.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    }

    default:
      return "";
  }
}

/**
 * Format a session start-time string to "hh:mm am/pm" in the given timezone.
 * Handles ISO timestamps ("2026-03-02T05:34:37+00:00") and firmware strings
 * ("2026-Mar-02 11:04 +0530", "06:30 AM", etc.).
 * Falls back to the browser's local timezone when timeZone is not provided.
 */
export function formatSessionTime(
  timeStr: string | null | undefined,
  timeZone?: string
): string {
  if (!timeStr) return "—";

  // Try as a proper Date (ISO timestamps, RFC strings)
  const d = new Date(timeStr);
  if (!isNaN(d.getTime())) {
    try {
      return new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
        .format(d)
        .toLowerCase();
    } catch {
      return d
        .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
        .toLowerCase();
    }
  }

  // Fallback: pull HH:MM + optional AM/PM out of any firmware-formatted string
  const m = timeStr.match(/(\d{1,2}:\d{2})\s*(AM|PM)?/i);
  if (m) return `${m[1]}${m[2] ? " " + m[2].toLowerCase() : ""}`;

  return timeStr;
}

// ---- Helpers ----

// Remove time safely
function toDateOnly(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Format WITHOUT UTC (no timezone shift)
function formatYYYYMMDD(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}
export const getSessionLabel = (s: any, idx = 0): string => {
  if (!s) return ""
  const date = new Date(s || "");
  if (isNaN(date.getTime())) return `S${idx + 1}`;
  // UTC logic as before
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  const hours12 = hours % 12 || 12;
  return `${hours12.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
};